/**
 * Notification Service
 *
 * Manages in-app notifications for the current user.
 * Provides real-time subscription, read/unread management, and navigation.
 *
 * Collection: users/{uid}/notifications/{notificationId}
 *
 * @see docs/features/IN-APP-NOTIFICATION-SYSTEM-SPEC.md
 */

import { Injectable, OnDestroy } from '@angular/core';
import { AngularFirestore, AngularFirestoreCollection, DocumentChangeAction } from '@newgenus/fire-compat/firestore';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, of } from 'rxjs';
import { catchError, map, takeUntil, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import firebase from 'firebase/compat/app';
import {
    InAppNotification,
    NotificationCategory,
    NotificationPreferences,
    DEFAULT_NOTIFICATION_PREFERENCES,
} from '@newgenus/common';
// Type-only: never imported as a runtime value, so NotificationService's constructor gains no
// new Angular DI dependency — every existing `TestBed.configureTestingModule({providers:
// [NotificationService, {provide: AngularFirestore, ...}, {provide: Router, ...}]})` in the spec
// suite keeps constructing the service unchanged. `attachCentralStore()` (below) is called
// imperatively, once, by whatever decides the issue tracker is on for this user (today:
// IssueTrackerAccessService) — a user without the tracker never calls it, so the env-only
// behaviour this class already has is untouched for them (ISSUE-TRACKER-V2-DESIGN.md §3).
import type { CentralSessionService } from '../../issues/central-session.service';
import type { IssuesFirebase } from '../../issues/central-firebase';

/** A notification as this service hands it out — tagged with which database it lives in so
 *  markAsRead/dismiss/markAllAsRead/dismissAll/handleNotificationClick route the write to the
 *  right Firestore project. Non-persisted: never written back, only stamped on read. */
export type StoredNotification = InAppNotification & { store: 'env' | 'issues' };

function toMillis(value: unknown): number {
    if (!value) return 0;
    const v = value as { toMillis?: () => number; seconds?: number };
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (typeof v.seconds === 'number') return v.seconds * 1000;
    return 0;
}

/**
 * Notification filter options
 */
export interface NotificationFilter {
    category?: NotificationCategory;
    isRead?: boolean;
    includeExpired?: boolean;
    limit?: number;
}

/**
 * Notification state snapshot
 */
export interface NotificationState {
    notifications: InAppNotification[];
    unreadCount: number;
    loading: boolean;
    error: string | null;
    hasMore: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class NotificationService implements OnDestroy {
    // === State Subjects ===
    private readonly notificationsSubject = new BehaviorSubject<InAppNotification[]>([]);
    private readonly unreadCountSubject = new BehaviorSubject<number>(0);
    private readonly loadingSubject = new BehaviorSubject<boolean>(false);
    private readonly errorSubject = new BehaviorSubject<string | null>(null);
    private readonly hasMoreSubject = new BehaviorSubject<boolean>(true);

    // === Public Observables ===
    public readonly notifications$ = this.notificationsSubject.asObservable();
    public readonly unreadCount$ = this.unreadCountSubject.asObservable();
    public readonly loading$ = this.loadingSubject.asObservable();
    public readonly error$ = this.errorSubject.asObservable();
    public readonly hasMore$ = this.hasMoreSubject.asObservable();

    /**
     * Combined state observable for components
     */
    public readonly state$: Observable<NotificationState> = combineLatest([
        this.notifications$,
        this.unreadCount$,
        this.loading$,
        this.error$,
        this.hasMore$,
    ]).pipe(
        map(([notifications, unreadCount, loading, error, hasMore]) => ({
            notifications,
            unreadCount,
            loading,
            error,
            hasMore,
        }))
    );

    // === Internal State ===
    private currentUid: string | null = null;
    private subscription: Subscription | null = null;
    private readonly destroy$ = new Subject<void>();
    private readonly DEFAULT_LIMIT = 50;
    private lastDoc: firebase.firestore.DocumentSnapshot | null = null;

    // === Central store merge (ISSUE-TRACKER-V2-DESIGN.md §3) ===
    /** The two sources merged into `notificationsSubject`/`unreadCountSubject`. */
    private envNotifications: StoredNotification[] = [];
    private centralNotifications: StoredNotification[] = [];
    private centralFirestore: firebase.firestore.Firestore | null = null;
    private centralUid: string | null = null;
    /** The inner per-centralUid listener — NOT covered by `takeUntil(this.destroy$)` (it's
     *  created imperatively inside the outer subscription's callback), so ngOnDestroy tears it
     *  down explicitly. */
    private centralSub: Subscription | null = null;
    private centralAttached = false;

    constructor(
        private readonly db: AngularFirestore,
        private readonly router: Router
    ) {}

    /**
     * Starts merging `centralUsers/{centralUid}/notifications` (the central "issues" project)
     * into the bell alongside the env stream — called once, imperatively, by whatever decides
     * the issue tracker is on for this user (today: IssueTrackerAccessService, from the header,
     * next to `notificationService.initialize(uid)`). Re-subscribes whenever the central
     * session's identity changes (sign-in/out, re-exchange); a session that never becomes ready
     * simply never contributes central notifications — the env behaviour is unaffected.
     */
    public attachCentralStore(centralSession: CentralSessionService, issuesFirebase: IssuesFirebase): void {
        if (this.centralAttached) return;
        this.centralAttached = true;
        this.centralFirestore = issuesFirebase.firestore;

        centralSession.centralUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
            this.centralSub?.unsubscribe();
            this.centralSub = null;
            this.centralUid = user?.uid ?? null;
            this.centralNotifications = [];
            this.recomputeMerged();
            if (!user) return;
            this.centralSub = this.subscribeCentralNotifications(issuesFirebase, user.uid);
        });
    }

    private subscribeCentralNotifications(issuesFirebase: IssuesFirebase, centralUid: string): Subscription {
        const query = issuesFirebase.firestore
            .collection(`centralUsers/${centralUid}/notifications`)
            .where('isDismissed', '==', false)
            .orderBy('createdAt', 'desc')
            .limit(this.DEFAULT_LIMIT);

        return new Observable<StoredNotification[]>((subscriber) => {
            return query.onSnapshot(
                (snap) =>
                    subscriber.next(
                        snap.docs.map((d) => ({
                            ...(d.data() as InAppNotification),
                            key: d.id,
                            store: 'issues' as const,
                        }))
                    ),
                (err: unknown) => subscriber.error(err)
            );
        })
            .pipe(
                catchError((error) => {
                    console.error('[NotificationService] Error fetching central (issues) notifications:', error);
                    return of([] as StoredNotification[]);
                })
            )
            .subscribe((notifications) => {
                this.centralNotifications = notifications;
                this.recomputeMerged();
            });
    }

    /** Merges env + central lists (newest first) into the public `notificationsSubject`/
     *  `unreadCountSubject` — the single recompute point every mutation below goes through. */
    private recomputeMerged(): void {
        const merged = [...this.envNotifications, ...this.centralNotifications].sort(
            (a, b) => toMillis(b.createdAt) - toMillis(a.createdAt)
        );
        this.notificationsSubject.next(merged);
        this.updateUnreadCount(merged);
    }

    /** Which database `key` (or a notification carrying it) lives in — defaults to 'env' for
     *  anything not tagged (every notification this service hands out IS tagged; this is a
     *  defensive fallback for a caller-supplied object built outside this service). */
    private storeOf(keyOrNotification: string | InAppNotification): 'env' | 'issues' {
        if (typeof keyOrNotification !== 'string') {
            const existing = (keyOrNotification as Partial<StoredNotification>).store;
            if (existing === 'env' || existing === 'issues') return existing;
        }
        const key = typeof keyOrNotification === 'string' ? keyOrNotification : keyOrNotification.key;
        const found = this.notificationsSubject.value.find((n) => n.key === key) as StoredNotification | undefined;
        return found?.store ?? 'env';
    }

    /**
     * Initialize notification subscription for a user
     * Call this when user logs in or changes
     */
    public initialize(uid: string): void {
        if (this.currentUid === uid) {
            return; // Already subscribed to this user
        }

        console.log('[NotificationService] Initializing for user:', uid);
        this.cleanup();
        this.currentUid = uid;
        this.loadingSubject.next(true);
        this.errorSubject.next(null); // Clear any previous error at start

        // Subscribe to notifications with real-time updates
        this.subscription = this.getNotificationsQuery(uid)
            .snapshotChanges()
            .pipe(
                takeUntil(this.destroy$),
                map((actions: DocumentChangeAction<InAppNotification>[]) => {
                    return actions.map((a) => {
                        const data = a.payload.doc.data() as InAppNotification;
                        return { ...data, key: a.payload.doc.id, store: 'env' as const };
                    });
                }),
                tap((notifications) => {
                    this.envNotifications = notifications;
                    this.recomputeMerged();
                    this.loadingSubject.next(false);
                    this.errorSubject.next(null); // Clear any previous error
                    this.hasMoreSubject.next(notifications.length >= this.DEFAULT_LIMIT);
                }),
                catchError((error) => {
                    console.error('[NotificationService] Error fetching notifications:', error);
                    // For missing index or permission errors, just show empty state
                    // This allows the UI to work even before the collection exists
                    this.envNotifications = [];
                    this.recomputeMerged();
                    this.loadingSubject.next(false);
                    // Only show error for unexpected failures, not "missing index" or permission errors
                    if (error?.code === 'failed-precondition' || error?.code === 'permission-denied') {
                        // Clear error - these are expected when collection doesn't exist or index is missing
                        this.errorSubject.next(null);
                    } else {
                        this.errorSubject.next('Failed to load notifications');
                    }
                    return of([]);
                })
            )
            .subscribe();
    }

    /**
     * Get Firestore query for notifications
     */
    private getNotificationsQuery(uid: string): AngularFirestoreCollection<InAppNotification> {
        return this.db.collection<InAppNotification>(`users/${uid}/notifications`, (ref) =>
            ref.where('isDismissed', '==', false).orderBy('createdAt', 'desc').limit(this.DEFAULT_LIMIT)
        );
    }

    /**
     * Update unread count from notifications
     */
    private updateUnreadCount(notifications: InAppNotification[]): void {
        const unreadCount = notifications.filter((n) => !n.isRead).length;
        this.unreadCountSubject.next(unreadCount);
    }

    /**
     * Load more notifications (pagination)
     */
    public async loadMore(): Promise<void> {
        if (!this.currentUid || !this.hasMoreSubject.value) {
            return;
        }

        const currentNotifications = this.envNotifications;
        if (currentNotifications.length === 0) {
            return;
        }

        this.loadingSubject.next(true);

        try {
            const lastNotification = currentNotifications[currentNotifications.length - 1];
            const lastDocRef = await this.db
                .doc(`users/${this.currentUid}/notifications/${lastNotification.key}`)
                .get()
                .toPromise();

            if (!lastDocRef?.exists) {
                this.hasMoreSubject.next(false);
                return;
            }

            const moreSnapshot = await this.db.firestore
                .collection(`users/${this.currentUid}/notifications`)
                .where('isDismissed', '==', false)
                .orderBy('createdAt', 'desc')
                .startAfter(lastDocRef)
                .limit(this.DEFAULT_LIMIT)
                .get();

            if (moreSnapshot.empty) {
                this.hasMoreSubject.next(false);
            } else {
                const moreNotifications = moreSnapshot.docs.map((doc) => ({
                    ...(doc.data() as InAppNotification),
                    key: doc.id,
                    store: 'env' as const,
                }));

                this.envNotifications = [...currentNotifications, ...moreNotifications];
                this.recomputeMerged();
                this.hasMoreSubject.next(moreNotifications.length >= this.DEFAULT_LIMIT);
            }
        } catch (error) {
            console.error('[NotificationService] Error loading more notifications:', error);
            this.errorSubject.next('Failed to load more notifications');
        } finally {
            this.loadingSubject.next(false);
        }
    }

    /**
     * Mark a notification as read
     */
    public async markAsRead(notificationKey: string): Promise<void> {
        if (!this.currentUid) {
            return;
        }

        try {
            const readAt = firebase.firestore.FieldValue.serverTimestamp();
            if (this.storeOf(notificationKey) === 'issues' && this.centralFirestore && this.centralUid) {
                await this.centralFirestore
                    .doc(`centralUsers/${this.centralUid}/notifications/${notificationKey}`)
                    .update({ isRead: true, readAt });
            } else {
                const notificationRef = this.db.doc(`users/${this.currentUid}/notifications/${notificationKey}`);
                await notificationRef.update({
                    isRead: true,
                    readAt,
                });
            }

            // Optimistic update — cast timestamp to match interface type.
            this.applyLocalPatch(notificationKey, {
                isRead: true,
                readAt: new Date() as unknown as InAppNotification['readAt'],
            });
        } catch (error) {
            console.error('[NotificationService] Error marking as read:', error);
            throw error;
        }
    }

    /** Patches `key` in whichever of envNotifications/centralNotifications holds it, then
     *  recomputes the merged public state. */
    private applyLocalPatch(key: string, patch: Partial<InAppNotification>): void {
        let touched = false;
        this.envNotifications = this.envNotifications.map((n) => {
            if (n.key !== key) return n;
            touched = true;
            return { ...n, ...patch };
        });
        if (!touched) {
            this.centralNotifications = this.centralNotifications.map((n) => (n.key === key ? { ...n, ...patch } : n));
        }
        this.recomputeMerged();
    }

    /**
     * Mark all notifications as read
     */
    public async markAllAsRead(): Promise<void> {
        if (!this.currentUid) {
            return;
        }

        const unreadNotifications = this.notificationsSubject.value.filter((n) => !n.isRead);
        if (unreadNotifications.length === 0) {
            return;
        }

        try {
            const now = firebase.firestore.FieldValue.serverTimestamp();
            const envUnread = unreadNotifications.filter((n) => this.storeOf(n) !== 'issues');
            const issuesUnread = unreadNotifications.filter((n) => this.storeOf(n) === 'issues');

            if (envUnread.length) {
                const batch = this.db.firestore.batch();
                for (const notification of envUnread) {
                    const ref = this.db.firestore.doc(`users/${this.currentUid}/notifications/${notification.key}`);
                    batch.update(ref, { isRead: true, readAt: now });
                }
                await batch.commit();
            }
            if (issuesUnread.length && this.centralFirestore && this.centralUid) {
                const batch = this.centralFirestore.batch();
                for (const notification of issuesUnread) {
                    const ref = this.centralFirestore.doc(
                        `centralUsers/${this.centralUid}/notifications/${notification.key}`
                    );
                    batch.update(ref, { isRead: true, readAt: now });
                }
                await batch.commit();
            }

            // Optimistic update - cast timestamp to match interface type
            const patch = { isRead: true, readAt: new Date() as unknown as InAppNotification['readAt'] };
            this.envNotifications = this.envNotifications.map((n) => ({ ...n, ...patch }));
            this.centralNotifications = this.centralNotifications.map((n) => ({ ...n, ...patch }));
            this.recomputeMerged();
        } catch (error) {
            console.error('[NotificationService] Error marking all as read:', error);
            throw error;
        }
    }

    /**
     * Dismiss a notification
     */
    public async dismiss(notificationKey: string): Promise<void> {
        if (!this.currentUid) {
            return;
        }

        try {
            const dismissedAt = firebase.firestore.FieldValue.serverTimestamp();
            if (this.storeOf(notificationKey) === 'issues' && this.centralFirestore && this.centralUid) {
                await this.centralFirestore
                    .doc(`centralUsers/${this.centralUid}/notifications/${notificationKey}`)
                    .update({ isDismissed: true, dismissedAt });
            } else {
                const notificationRef = this.db.doc(`users/${this.currentUid}/notifications/${notificationKey}`);
                await notificationRef.update({
                    isDismissed: true,
                    dismissedAt,
                });
            }

            // Remove from local state (real-time listener will also update)
            this.envNotifications = this.envNotifications.filter((n) => n.key !== notificationKey);
            this.centralNotifications = this.centralNotifications.filter((n) => n.key !== notificationKey);
            this.recomputeMerged();
        } catch (error) {
            console.error('[NotificationService] Error dismissing notification:', error);
            throw error;
        }
    }

    /**
     * Dismiss all notifications
     */
    public async dismissAll(): Promise<void> {
        if (!this.currentUid) {
            return;
        }

        const notifications = this.notificationsSubject.value;
        if (notifications.length === 0) {
            return;
        }

        try {
            const now = firebase.firestore.FieldValue.serverTimestamp();
            const envOnes = notifications.filter((n) => this.storeOf(n) !== 'issues');
            const issuesOnes = notifications.filter((n) => this.storeOf(n) === 'issues');

            if (envOnes.length) {
                const batch = this.db.firestore.batch();
                for (const notification of envOnes) {
                    const ref = this.db.firestore.doc(`users/${this.currentUid}/notifications/${notification.key}`);
                    batch.update(ref, { isDismissed: true, dismissedAt: now });
                }
                await batch.commit();
            }
            if (issuesOnes.length && this.centralFirestore && this.centralUid) {
                const batch = this.centralFirestore.batch();
                for (const notification of issuesOnes) {
                    const ref = this.centralFirestore.doc(
                        `centralUsers/${this.centralUid}/notifications/${notification.key}`
                    );
                    batch.update(ref, { isDismissed: true, dismissedAt: now });
                }
                await batch.commit();
            }

            // Clear local state
            this.envNotifications = [];
            this.centralNotifications = [];
            this.recomputeMerged();
        } catch (error) {
            console.error('[NotificationService] Error dismissing all notifications:', error);
            throw error;
        }
    }

    /**
     * Handle notification click - mark as actioned and navigate
     */
    public async handleNotificationClick(notification: InAppNotification): Promise<void> {
        if (!this.currentUid) {
            return;
        }

        // Mark as read and actioned
        try {
            const patch = {
                isRead: true,
                readAt: firebase.firestore.FieldValue.serverTimestamp(),
                isActioned: true,
                actionedAt: firebase.firestore.FieldValue.serverTimestamp(),
            };
            if (this.storeOf(notification) === 'issues' && this.centralFirestore && this.centralUid) {
                await this.centralFirestore
                    .doc(`centralUsers/${this.centralUid}/notifications/${notification.key}`)
                    .update(patch);
            } else {
                const notificationRef = this.db.doc(`users/${this.currentUid}/notifications/${notification.key}`);
                await notificationRef.update(patch);
            }
        } catch (error) {
            console.error('[NotificationService] Error updating notification on click:', error);
        }

        // Navigate if there's an action route
        if (notification.actionRoute) {
            if (notification.actionQueryParams) {
                this.router.navigate([notification.actionRoute], {
                    queryParams: notification.actionQueryParams,
                });
            } else {
                this.router.navigate([notification.actionRoute]);
            }
        }
    }

    /**
     * Get notifications by category
     */
    public getNotificationsByCategory(category: NotificationCategory): InAppNotification[] {
        return this.notificationsSubject.value.filter((n) => n.category === category);
    }

    /**
     * Get unread notifications
     */
    public getUnreadNotifications(): InAppNotification[] {
        return this.notificationsSubject.value.filter((n) => !n.isRead);
    }

    /**
     * Get notification by key
     */
    public getNotificationByKey(key: string): InAppNotification | undefined {
        return this.notificationsSubject.value.find((n) => n.key === key);
    }

    /**
     * Get notification preferences for user
     */
    public async getPreferences(): Promise<NotificationPreferences> {
        if (!this.currentUid) {
            return DEFAULT_NOTIFICATION_PREFERENCES;
        }

        try {
            const prefsDoc = await this.db.doc(`users/${this.currentUid}/settings/notifications`).get().toPromise();

            if (prefsDoc?.exists) {
                return prefsDoc.data() as NotificationPreferences;
            }

            return DEFAULT_NOTIFICATION_PREFERENCES;
        } catch (error) {
            console.error('[NotificationService] Error getting preferences:', error);
            return DEFAULT_NOTIFICATION_PREFERENCES;
        }
    }

    /**
     * Update notification preferences
     */
    public async updatePreferences(preferences: Partial<NotificationPreferences>): Promise<void> {
        if (!this.currentUid) {
            return;
        }

        try {
            const prefsRef = this.db.doc(`users/${this.currentUid}/settings/notifications`);
            await prefsRef.set(preferences, { merge: true });
        } catch (error) {
            console.error('[NotificationService] Error updating preferences:', error);
            throw error;
        }
    }

    /**
     * Get count by category
     */
    public getCountByCategory(): Map<NotificationCategory, { total: number; unread: number }> {
        const counts = new Map<NotificationCategory, { total: number; unread: number }>();
        const notifications = this.notificationsSubject.value;

        for (const notification of notifications) {
            const existing = counts.get(notification.category) || { total: 0, unread: 0 };
            existing.total++;
            if (!notification.isRead) {
                existing.unread++;
            }
            counts.set(notification.category, existing);
        }

        return counts;
    }

    /**
     * Cleanup subscriptions
     */
    private cleanup(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = null;
        }
        this.currentUid = null;
        this.envNotifications = [];
        this.recomputeMerged();
        this.errorSubject.next(null);
        this.hasMoreSubject.next(true);
        this.lastDoc = null;
    }

    /**
     * Cleanup on destroy
     */
    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        // Not covered by takeUntil(this.destroy$): created imperatively inside the outer
        // centralUser$ subscription's callback, not as part of that same observable chain.
        this.centralSub?.unsubscribe();
        this.centralSub = null;
        this.centralNotifications = [];
        this.cleanup();
    }
}
